import { ChapterLike, storyBlocksProps, storyDataArrayProps } from "../types";
import { WEB_SCREENS } from "../constants/screens";
import { WebStoryNavigation } from "../navigation/types";
import { isTablet } from "./scaleSize";
import { useNavigate } from "react-router-dom";

export function isLastBlockPending(
  chapters: ChapterLike[] | undefined,
): boolean {
  const validStatuses = ["running", "restart", "restarted"];
  if (!chapters?.length) {
    return false;
  }

  for (const chapter of chapters) {
    const blockCount = chapter.blocks.length;
    if (validStatuses.includes(chapter.status) && blockCount > 0) {
      const lastIndex = blockCount - 1;
      const allPreviousCompleted = chapter.blocks
        .slice(0, lastIndex)
        .every((b) => b.status === "completed");
      const lastBlockPending = chapter.blocks[lastIndex].status === "pending";
      if (allPreviousCompleted && lastBlockPending) {
        return true;
      }
    }
  }
  return false;
}

export function hasStandardBlockAfter(
  blocks: storyBlocksProps[] | undefined,
  chapters: ChapterLike[] | undefined,
): boolean {
  const currentIdx = 0;
  if (!Array.isArray(blocks)) {
    return false;
  }
  const currentBlockId = blocks?.[currentIdx]?.id;

  if (blocks?.[currentIdx]?.blockType?.toLowerCase() === "re-entry block") {
    return true;
  }

  const chapterWithBlock = chapters?.find((chapter) =>
    chapter?.blocks?.some((block) => block.id === currentBlockId),
  );

  if (!chapterWithBlock) {
    return false;
  }

  const blockIndexInChapter = chapterWithBlock.blocks.findIndex(
    (b) => b.id === currentBlockId,
  );
  const nextBlock = chapterWithBlock.blocks[blockIndexInChapter + 1];
  return !!nextBlock;
}

export const handleTemplateFlags = (
  templates: { name: string; status: boolean }[] | undefined,
  setFlags: {
    setEnablebagPack: (_v: boolean) => void;
    setEnableJournal: (_v: boolean) => void;
    setEnableMywall: (_v: boolean) => void;
    setEnableFunbox: (_v: boolean) => void;
    setBallonObject: (_v: boolean) => void;
  },
) => {
  templates?.forEach(({ name, status }) => {
    if (!status) {
      return;
    }
    switch (name) {
      case "backPackScreen":
        setFlags.setEnablebagPack(true);
        break;
      case "travelPlan":
        setFlags.setEnableJournal(true);
        break;
      case "wallScreen":
        setFlags.setEnableMywall(true);
        break;
      case "funBoxScreen":
        setFlags.setEnableFunbox(true);
        break;
      case "ballonScreen":
        setFlags.setBallonObject(true);
        break;
      default:
        break;
    }
  });
};

export interface LessonPlanQuestion {
  name: string;
  question: string;
  url: string;
}

export const navigateToStoryBreakScreen = (
  // navigation: WebStoryNavigation,
  storyDataArray: storyDataArrayProps,
  navigate: ReturnType<typeof useNavigate>,
  bgImageOverride?: string,
  lessonPlanQuestion?: LessonPlanQuestion,
) => {
  const milestones = storyDataArray?.data?.milestones;

  if (milestones?.length) {
    const milestoneValue = milestones[milestones.length - 1];
    navigate("/web/WebStoryBreakScreen", {
      state: {
        milestoneData: milestoneValue,
        bgImage: bgImageOverride ?? storyDataArray?.data?.blocks?.[0]?.bgImage,
        documentId: storyDataArray?.data?.documentId,
        storyId: storyDataArray?.storyId,
        lessonPlanQuestion,
      },
    });
  }
};

export const revertLastCompletedChapter = (chapters: ChapterLike[]) => {
  for (let i = chapters.length - 1; i >= 0; i--) {
    if (chapters[i].status === "completed") {
      return chapters[i];
    }
  }
  return chapters[0];
};

export const conversationBufferDuration = 2000;

export const convertStringToID = (linkTo: string): string => {
  const parts = linkTo?.split("/") ?? [];
  const last = parts[parts.length - 1] ?? linkTo;
  return last.startsWith("id:") ? last.slice(3) : last;
};

export function getRelatedBlockIDs(
  allBlocksData: any[],
  clickedBlockID: number,
) {
  const clickedBlock = allBlocksData.find(
    (block: { id: number }) => block.id === clickedBlockID,
  );
  if (!clickedBlock || !clickedBlock.parentBlock) {
    return []; // No parent block found or the block itself isn't found
  }

  const parentBlockID = clickedBlock.parentBlock.id;
  const blockIDs = allBlocksData
    .filter(
      (block: { parentBlock: { id: number } }) =>
        block.parentBlock && block.parentBlock.id === parentBlockID,
    )
    .map((block: { id: any }) => {
      return {
        id: block.id,
        status: "skipped",
      };
    });
  return [
    ...blockIDs,
    {
      id: parentBlockID,
      status: "completed",
    },
  ];
}

export const getBgImageForBlock = (
  block: { bgImage?: string; bgImageTab?: string } | undefined,
): string | undefined =>
  isTablet() ? (block?.bgImageTab ?? block?.bgImage) : block?.bgImage;

export const playTapSound = async (audioUrl: string) => {
  try {
    const audio = new Audio(audioUrl);
    await audio.play();
  } catch {
    // Ignore autoplay restrictions
  }
};
